import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { HeroSection } from './components/HeroSection';
import { PainPoints } from './components/PainPoints';
// Lazy load below-the-fold components
const SolutionIntro = lazy(() => import('./components/SolutionIntro').then(m => ({
  default: m.SolutionIntro
})));
const WhatYouReceive = lazy(() => import('./components/WhatYouReceive').then(m => ({
  default: m.WhatYouReceive
})));
const BonusSection = lazy(() => import('./components/BonusSection').then(m => ({
  default: m.BonusSection
})));
const PricingSection = lazy(() => import('./components/PricingSection').then(m => ({
  default: m.PricingSection
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const PurchaseSteps = lazy(() => import('./components/PurchaseSteps').then(m => ({
  default: m.PurchaseSteps
})));
const GuaranteeBeforeFAQ = lazy(() => import('./components/GuaranteeBeforeFAQ').then(m => ({
  default: m.GuaranteeBeforeFAQ
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialProofPopup = lazy(() => import('./components/SocialProofPopup').then(m => ({
  default: m.SocialProofPopup
})));
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: (...args: unknown[]) => void;
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}
// === CRITICAL: Run immediately at module load (before React renders) ===
// Preconnect to Google Fonts (only 2 preconnects to stay under limit)
const preconnect1 = document.createElement('link');
preconnect1.rel = 'preconnect';
preconnect1.href = 'https://fonts.googleapis.com';
document.head.appendChild(preconnect1);
const preconnect2 = document.createElement('link');
preconnect2.rel = 'preconnect';
preconnect2.href = 'https://fonts.gstatic.com';
preconnect2.crossOrigin = 'anonymous';
document.head.appendChild(preconnect2);
// Preload LCP hero image immediately
const preloadLink = document.createElement('link');
preloadLink.rel = 'preload';
preloadLink.as = 'image';
preloadLink.href = 'https://cdn.magicpatterns.com/uploads/sPCnVPMtXioPTct6J7b21f/MOCKUP_600x.png';
preloadLink.fetchPriority = 'high';
document.head.appendChild(preloadLink);
// Load Google Fonts asynchronously (non-render-blocking)
const fontLink = document.createElement('link');
fontLink.rel = 'stylesheet';
fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;700;800;900&family=Inter:wght@400;600;700&display=swap';
fontLink.media = 'print';
fontLink.onload = () => {
  fontLink.media = 'all';
};
document.head.appendChild(fontLink);
// === Load third-party scripts only after first user interaction ===
let thirdPartyLoaded = false;
function loadThirdPartyScripts() {
  if (thirdPartyLoaded) return;
  thirdPartyLoaded = true;
  // Remove interaction listeners
  window.removeEventListener('scroll', loadThirdPartyScripts);
  window.removeEventListener('click', loadThirdPartyScripts);
  window.removeEventListener('touchstart', loadThirdPartyScripts);
  // Google Analytics
  const gtagScript = document.createElement('script');
  gtagScript.async = true;
  gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-W2P4272296';
  document.head.appendChild(gtagScript);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: unknown[]) {
    window.dataLayer.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', 'G-W2P4272296');
  // Meta Pixel Code
  if (!window.fbq) {
    const n: any = window.fbq = function (...args: unknown[]) {
      if (n.callMethod) {
        n.callMethod.apply(n, args);
      } else {
        n.queue.push(args);
      }
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as unknown[][];
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const s = document.getElementsByTagName('script')[0];
    if (s && s.parentNode) {
      s.parentNode.insertBefore(t, s);
    }
  }
  window.fbq('init', '1687764378881323');
  window.fbq('track', 'PageView');
  // Noscript pixel fallback
  const noscriptImg = document.createElement('img');
  noscriptImg.height = 1;
  noscriptImg.width = 1;
  noscriptImg.style.display = 'none';
  noscriptImg.src = 'https://www.facebook.com/tr?id=1687764378881323&ev=PageView&noscript=1';
  noscriptImg.alt = '';
  document.body.appendChild(noscriptImg);
}
// Listen for first interaction, with fallback timeout
window.addEventListener('scroll', loadThirdPartyScripts, {
  once: true,
  passive: true
});
window.addEventListener('click', loadThirdPartyScripts, {
  once: true
});
window.addEventListener('touchstart', loadThirdPartyScripts, {
  once: true,
  passive: true
});
// Fallback: load after 5 seconds if no interaction
setTimeout(loadThirdPartyScripts, 5000);
export function App() {
  return <div className="w-full min-h-screen bg-white" data-id="element-0">
      <HeroSection data-id="element-1" />
      <PainPoints data-id="element-2" />
      <Suspense fallback={null} data-id="element-3">
        <SolutionIntro data-id="element-4" />
        <WhatYouReceive data-id="element-5" />
        <BonusSection data-id="element-6" />
        <PricingSection data-id="element-7" />
        <Testimonials data-id="element-8" />
        <Guarantee data-id="element-9" />
        <PurchaseSteps data-id="element-10" />
        <GuaranteeBeforeFAQ data-id="element-11" />
        <FAQ data-id="element-12" />
        <FinalCTA data-id="element-13" />
        <Footer data-id="element-14" />
        <SocialProofPopup data-id="element-15" />
      </Suspense>
    </div>;
}